import {
  type ApolloQueryResult,
  type FetchResult,
  type OperationVariables,
  useMutation,
  useQuery,
} from '@apollo/client';
import { adaptV4Theme, CircularProgress, Paper, type Theme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { alpha, createTheme, lighten, StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { ThemeProvider as StylesThemeProvider } from '@mui/styles';
import makeStyles from '@mui/styles/makeStyles';
import { createRootRouteWithContext, Outlet } from '@tanstack/react-router';
import { TanStackRouterDevtools } from '@tanstack/router-devtools';
import React, { type ReactElement, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { type IIdleTimer, useIdleTimer } from 'react-idle-timer';
import { refreshToken } from 'src/api/auth';
import { allConfigs } from 'src/api/configurations';
import allThemesFiltered from 'src/api/theme/allThemes';
import RightClickMenu from 'src/components/RightClickMenu/RightClickMenu';
import { DEFAULT_THEME_NAME, FAV_ICON_OPTION_KEY, FAVICON_LOGO_API } from 'src/constants';
import AuthContext from 'src/context/AuthContext';
import { buildLicencseRequestQuery } from 'src/dynamoQueries/license';
import {
  getTokenAndExpire,
  getTokenExpirationMinutes,
  REFRESH_EXPIRES_IN,
  setTokenAndExpire,
  TOKEN,
  TOKEN_EXPIRES,
  tokenCanBeRefreshed,
  useLogout,
} from 'src/helpers/authHelper';
import theme from 'src/theme';

// Router context type for auth state
export interface RouterContext {
  isAuthenticated: boolean;
  hasValidLicense: boolean | undefined;
  userRole: string | undefined;
}

interface ThemeColor {
  main?: string;
  light?: string;
  dark?: string;
  contrastText?: string;
}

interface ThemeNode {
  name: string;
  primary?: ThemeColor | string;
  secondary?: ThemeColor | string;
}

interface ThemeEdge {
  node: ThemeNode;
}

interface AllThemesData {
  allThemes: {
    edges: ThemeEdge[];
  };
}

interface ConfigurationNode {
  optionValue: string;
}

interface ConfigurationEdge {
  node: ConfigurationNode;
}

interface AllConfigurationsData {
  allConfigurations: {
    edges: ConfigurationEdge[];
  };
}

interface LicenseData {
  license?: {
    isValid: boolean;
  };
}

interface RefreshTokenPayload {
  exp: number;
}

interface RefreshTokenResponse {
  refreshToken: {
    token: string;
    refreshExpiresIn: number;
    payload: RefreshTokenPayload;
  };
}

interface CachedThemeColors {
  primary?: ThemeColor | string;
  secondary?: ThemeColor | string;
}

const CACHED_THEME_COLORS_KEY = 'akomi_theme_colors_v1';

const useStyles = makeStyles(() => ({
  beforeLoading: {
    backgroundColor: theme.palette?.background?.default ?? '#121212',
    height: '100vh',
    width: '100vw',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

const resolveThemeColor = (value: ThemeColor | string | undefined, fallback: ThemeColor): ThemeColor => {
  const defaultColor: ThemeColor = {
    light: fallback.light,
    main: fallback.main,
    dark: fallback.dark,
    contrastText: fallback.contrastText,
  };

  if (!value) {
    return defaultColor;
  }

  let parsed: ThemeColor | string = value;
  if (typeof parsed === 'string') {
    const rawValue = parsed;
    try {
      parsed = JSON.parse(parsed) as ThemeColor;
    } catch {
      const rawColor = rawValue.trim();
      return rawColor ? { ...defaultColor, main: rawColor } : defaultColor;
    }
  }

  if (typeof parsed === 'object' && parsed !== null) {
    return {
      ...defaultColor,
      ...(parsed.light ? { light: parsed.light } : {}),
      ...(parsed.main ? { main: parsed.main } : {}),
      ...(parsed.dark ? { dark: parsed.dark } : {}),
      ...(parsed.contrastText ? { contrastText: parsed.contrastText } : {}),
    };
  }

  return defaultColor;
};

const getCachedThemeColors = (): CachedThemeColors | undefined => {
  try {
    const rawValue = localStorage.getItem(CACHED_THEME_COLORS_KEY);
    if (!rawValue) return undefined;
    const parsed = JSON.parse(rawValue) as CachedThemeColors;
    if (!parsed || (parsed.primary === undefined && parsed.secondary === undefined)) return undefined;
    return parsed;
  } catch {
    return undefined;
  }
};

const setCachedThemeColors = (colors: CachedThemeColors): void => {
  try {
    localStorage.setItem(CACHED_THEME_COLORS_KEY, JSON.stringify(colors));
  } catch {
    // Ignore localStorage failures (e.g. private mode restrictions).
  }
};

const buildMainTheme = (primary?: ThemeColor | string, secondary?: ThemeColor | string): Theme => {
  const fallbackPrimary = (theme.palette?.primary ?? {
    light: '#DBB2FF',
    main: '#BB86FC',
    dark: '#9a67ea',
    contrastText: '#ffffff',
  }) as ThemeColor;

  const fallbackSecondary = (theme.palette?.secondary ?? {
    light: '#A8DDDA',
    main: '#67CBC5',
    dark: '#4fb3ad',
    contrastText: '#ffffff',
  }) as ThemeColor;

  const resolvedPrimary = resolveThemeColor(primary, fallbackPrimary);
  const resolvedSecondary = resolveThemeColor(secondary, fallbackSecondary);

  const updatedTheme = {
    ...theme,
    palette: {
      ...theme.palette,
      primary: {
        ...(theme.palette?.primary ?? {}),
        ...resolvedPrimary,
        main: resolvedPrimary.main ?? fallbackPrimary.main ?? '#BB86FC',
      },
      secondary: {
        ...(theme.palette?.secondary ?? {}),
        ...resolvedSecondary,
        main: resolvedSecondary.main ?? fallbackSecondary.main ?? '#67CBC5',
      },
    },
  };

  const adaptedTheme = adaptV4Theme(updatedTheme);
  const primaryMain = resolvedPrimary.main ?? fallbackPrimary.main ?? '#BB86FC';
  const primaryDark = resolvedPrimary.dark ?? fallbackPrimary.dark ?? primaryMain;
  const primaryContrast = resolvedPrimary.contrastText ?? fallbackPrimary.contrastText ?? '#ffffff';
  const secondaryMain = resolvedSecondary.main ?? fallbackSecondary.main ?? '#67CBC5';
  const secondaryDark = resolvedSecondary.dark ?? fallbackSecondary.dark ?? secondaryMain;
  const secondaryContrast = resolvedSecondary.contrastText ?? fallbackSecondary.contrastText ?? '#ffffff';
  const existingCssBaselineStyleOverrides = (
    adaptedTheme.components?.MuiCssBaseline as { styleOverrides?: Record<string, unknown> } | undefined
  )?.styleOverrides;

  return createTheme({
    ...adaptedTheme,
    components: {
      ...adaptedTheme.components,
      MuiCssBaseline: {
        styleOverrides: {
          ...(existingCssBaselineStyleOverrides ?? {}),
          ':root': {
            '--akomi-rdr-date-input-bg': adaptedTheme.palette?.background?.paper ?? '#232323',
            '--akomi-rdr-display-wrapper-bg': adaptedTheme.palette?.grey?.['400'] ?? '#505050',
            '--akomi-rdr-calendar-bg': adaptedTheme.palette?.grey?.['500'] ?? '#343434',
            '--akomi-rdr-defined-ranges-bg': adaptedTheme.palette?.grey?.['400'] ?? '#505050',
            '--akomi-rdr-static-range-bg': adaptedTheme.palette?.grey?.['300'] ?? '#646464',
            '--akomi-rdr-month-year-bg': adaptedTheme.palette?.background?.paper ?? '#232323',
            '--akomi-rdr-weekdays-bg': adaptedTheme.palette?.background?.navbar ?? '#1C1C1C',
            '--akomi-rdr-text-color': adaptedTheme.palette?.text?.primary ?? '#ffffff',
            '--akomi-text-primary': adaptedTheme.palette?.text?.primary ?? '#ffffff',
            '--akomi-text-secondary': adaptedTheme.palette?.text?.secondary ?? 'rgba(255,255,255,0.7)',
            '--akomi-text-disabled': adaptedTheme.palette?.text?.disabled ?? '#999999',
            '--akomi-background-paper': adaptedTheme.palette?.background?.paper ?? '#232323',
            '--akomi-background-default': adaptedTheme.palette?.background?.default ?? '#121212',
            '--akomi-divider': adaptedTheme.palette?.divider ?? 'rgba(255,255,255,0.12)',
            '--akomi-primary-main': adaptedTheme.palette?.primary?.main ?? '#BB86FC',
            '--akomi-primary-light': adaptedTheme.palette?.primary?.light ?? '#DBB2FF',
            '--akomi-primary-dark': adaptedTheme.palette?.primary?.dark ?? '#9a67ea',
            '--akomi-secondary-main': adaptedTheme.palette?.secondary?.main ?? '#67CBC5',
            '--akomi-secondary-light': adaptedTheme.palette?.secondary?.light ?? '#A8DDDA',
            '--akomi-secondary-dark': adaptedTheme.palette?.secondary?.dark ?? '#4fb3ad',
            '--akomi-success-main': adaptedTheme.palette?.success?.main ?? '#4CAF50',
            '--akomi-warning-main': adaptedTheme.palette?.warning?.main ?? '#FF9800',
            '--akomi-info-main': adaptedTheme.palette?.info?.main ?? '#2196F3',
            '--akomi-error-main': adaptedTheme.palette?.error?.main ?? '#FF5252',
            '--akomi-dropzone-border': adaptedTheme.palette?.divider ?? 'rgba(255, 255, 255, 0.12)',
            '--akomi-dropzone-bg': adaptedTheme.palette?.action?.hover ?? 'rgba(255, 255, 255, 0.02)',
            '--akomi-dropzone-hover-border': alpha(adaptedTheme.palette?.primary?.main ?? '#BB86FC', 0.6),
            '--akomi-dropzone-hover-bg': alpha(adaptedTheme.palette?.primary?.main ?? '#BB86FC', 0.06),
            '--akomi-dropzone-hover-shadow': alpha(adaptedTheme.palette?.primary?.main ?? '#BB86FC', 0.12),
          },
        },
      },
      MuiTextField: { defaultProps: { size: 'small' } },
      MuiSelect: { defaultProps: { size: 'small' } },
      MuiAutocomplete: { defaultProps: { size: 'small' } },
      MuiFormControl: { defaultProps: { size: 'small' } },
      MuiInputBase: {
        defaultProps: { size: 'small' },
        styleOverrides: {
          root: {
            color: adaptedTheme.palette?.text?.primary,
            '& input::placeholder, & textarea::placeholder': {
              color: adaptedTheme.palette?.text?.disabled,
              opacity: 1,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: adaptedTheme.palette?.text?.secondary,
            '&.Mui-focused': {
              color: secondaryMain,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            color: adaptedTheme.palette?.text?.secondary,
            '&.Mui-focused': {
              color: secondaryMain,
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-notchedOutline': {
              borderColor: adaptedTheme.palette?.divider,
            },
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: secondaryMain,
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: secondaryMain,
            },
          },
          input: {
            color: adaptedTheme.palette?.text?.primary,
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          underline: {
            '&:before': {
              borderBottom: `1px solid ${adaptedTheme.palette?.divider ?? 'rgba(255,255,255,0.3)'}`,
            },
            '&:after': {
              borderBottom: `2px solid ${secondaryMain}`,
            },
            '&:hover:not(.Mui-disabled, .Mui-error):before': {
              borderBottom: `2px solid ${secondaryMain}`,
            },
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          root: {
            color: adaptedTheme.palette?.text?.disabled,
            '&.Mui-checked': {
              color: primaryMain,
            },
          },
        },
      },
      MuiSwitch: {
        styleOverrides: {
          switchBase: {
            color: adaptedTheme.palette?.text?.disabled,
            '&.Mui-checked': {
              color: primaryMain,
              '& + .MuiSwitch-track': {
                backgroundColor: primaryMain,
                opacity: 1,
              },
            },
          },
          track: {
            backgroundColor: adaptedTheme.palette?.text?.disabled,
            opacity: 0.4,
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          contained: {
            backgroundColor: primaryMain,
            color: primaryContrast,
            '&:hover': {
              backgroundColor: lighten(primaryMain, 0.2),
            },
          },
          containedPrimary: {
            backgroundColor: primaryMain,
            color: primaryContrast,
            '&:hover': {
              backgroundColor: lighten(primaryMain, 0.2),
            },
          },
          containedSecondary: {
            backgroundColor: secondaryMain,
            color: secondaryContrast,
            '&:hover': {
              backgroundColor: lighten(secondaryMain, 0.2),
            },
          },
          outlinedPrimary: {
            color: primaryMain,
            borderColor: primaryMain,
            '&:hover': {
              borderColor: primaryMain,
              backgroundColor: alpha(primaryMain, 0.12),
            },
          },
          outlinedSecondary: {
            color: secondaryMain,
            borderColor: secondaryMain,
            '&:hover': {
              borderColor: secondaryMain,
              backgroundColor: alpha(secondaryMain, 0.12),
            },
          },
          textPrimary: {
            color: primaryMain,
            '&:hover': {
              backgroundColor: alpha(primaryMain, 0.12),
            },
          },
          textSecondary: {
            color: secondaryMain,
            '&:hover': {
              backgroundColor: alpha(secondaryMain, 0.12),
            },
          },
        },
      },
    },
  });
};

function RootComponent(): ReactElement {
  const tokenRefreshThreshold = 2;
  const classes = useStyles();
  const tokenData = getTokenAndExpire();
  const token = tokenData[TOKEN];
  const [hasToken, setHasToken] = useState<boolean>(!!token);
  const [tokenExpiration, setTokenExpiration] = useState<number>(0);
  const [mainTheme, setMainTheme] = useState<Theme>(() => {
    const cachedThemeColors = getCachedThemeColors();
    return buildMainTheme(cachedThemeColors?.primary, cachedThemeColors?.secondary);
  });
  const [sendRefreshTokenRequest] = useMutation<RefreshTokenResponse>(refreshToken);

  const {
    data: licenseData,
    loading: loadingLicense,
    refetch,
  } = useQuery<LicenseData>(buildLicencseRequestQuery(), {
    fetchPolicy: 'no-cache',
  });
  const logout = useLogout(() => setHasToken(false));

  const { data: icon } = useQuery<AllConfigurationsData>(allConfigs, {
    variables: {
      optionKey: FAV_ICON_OPTION_KEY,
    },
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent): void => {
      if (e.keyCode === 32 && e.target === document.body) {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const { data: siteTitle } = useQuery<AllConfigurationsData>(allConfigs, {
    variables: {
      optionKey: 'site_title',
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: siteDescrption } = useQuery<AllConfigurationsData>(allConfigs, {
    variables: {
      optionKey: 'site_description',
    },
    fetchPolicy: 'cache-and-network',
  });

  const { data: themes, loading: loadingThemes } = useQuery<AllThemesData>(allThemesFiltered, {
    variables: { name: DEFAULT_THEME_NAME },
  });

  const refreshAccessToken = (): void => {
    const tokenMeta = getTokenAndExpire();
    const currentToken = tokenMeta[TOKEN];
    const refreshExp = tokenMeta[REFRESH_EXPIRES_IN];
    if (currentToken && tokenCanBeRefreshed(Number(refreshExp))) {
      sendRefreshTokenRequest({
        variables: { token: currentToken },
      })
        .then((resp: FetchResult<RefreshTokenResponse>) => {
          if (resp.data?.refreshToken) {
            setTokenAndExpire(
              resp.data.refreshToken.token,
              resp.data.refreshToken.refreshExpiresIn,
              resp.data.refreshToken.payload.exp,
            );
            const tokenExp = getTokenExpirationMinutes(resp.data.refreshToken.payload.exp);
            setTokenExpiration(tokenExp);
          }
        })
        .catch(() => {
          logout();
        });
    } else {
      logout();
    }
  };

  const refreshTokenTimerOnExpire = (): void => {
    refreshAccessToken();
  };

  const refreshTokenTimer: IIdleTimer = useIdleTimer({
    startManually: true,
    startOnMount: false,
    stopOnIdle: true,
    timeout: tokenExpiration ? 1000 * 60 * tokenExpiration - 60 * 1000 * tokenRefreshThreshold : 60 * 1000,
    events: [],
    onIdle: refreshTokenTimerOnExpire,
  });

  useEffect(() => {
    if (hasToken && !tokenExpiration) {
      const tokenMeta = getTokenAndExpire();
      const tokenExpiresValue = tokenMeta[TOKEN_EXPIRES];
      const tokenExp = getTokenExpirationMinutes(Number(tokenExpiresValue));
      if (tokenExp < tokenRefreshThreshold) {
        logout();
      } else {
        setTokenExpiration(tokenExp);
      }
    }
  }, [hasToken, tokenExpiration, logout]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshTokenTimer.reset is stable and we only want to trigger on tokenExpiration changes
  useEffect(() => {
    if (tokenExpiration) {
      refreshTokenTimer.reset();
    }
  }, [tokenExpiration]);

  useEffect(() => {
    if (themes?.allThemes?.edges) {
      const defaultTheme = themes.allThemes.edges.find((x) => x.node.name === DEFAULT_THEME_NAME);
      if (defaultTheme) {
        setCachedThemeColors({
          primary: defaultTheme.node.primary,
          secondary: defaultTheme.node.secondary,
        });
        setMainTheme(buildMainTheme(defaultTheme.node.primary, defaultTheme.node.secondary));
      }
    }
  }, [themes]);

  if (loadingLicense || loadingThemes) {
    return (
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={mainTheme}>
          <StylesThemeProvider theme={mainTheme}>
            <CssBaseline />
            <Paper className={classes.beforeLoading}>
              <CircularProgress color="secondary" />
            </Paper>
          </StylesThemeProvider>
        </ThemeProvider>
      </StyledEngineProvider>
    );
  }

  const handleRefetchLicense = (variables?: Partial<OperationVariables>): Promise<ApolloQueryResult<LicenseData>> => {
    return refetch(variables);
  };

  return (
    <React.Fragment>
      <RightClickMenu />
      <Helmet
        titleTemplate={
          siteTitle?.allConfigurations.edges[0]?.node.optionValue
            ? `${siteTitle.allConfigurations.edges[0].node.optionValue} - %s`
            : `Akomi v3 - %s`
        }
        defaultTitle={siteTitle?.allConfigurations.edges[0]?.node.optionValue}
      />
      <Helmet>
        {icon?.allConfigurations?.edges[0] ? <link href={FAVICON_LOGO_API} rel="icon" /> : null}
        <meta
          name="description"
          content={
            siteDescrption?.allConfigurations.edges[0]?.node.optionValue
              ? siteDescrption.allConfigurations.edges[0].node.optionValue
              : 'Akomi'
          }
        />
      </Helmet>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={mainTheme}>
          <StylesThemeProvider theme={mainTheme}>
            <CssBaseline />
            <AuthContext.Provider
              value={{
                hasToken: hasToken,
                setHasToken: setHasToken,
                setTokenExpiration: setTokenExpiration,
                refreshTokenTimer: refreshTokenTimer,
                validLicense: licenseData?.license?.isValid,
                refetchLicense: handleRefetchLicense,
              }}
            >
              <Outlet />
            </AuthContext.Provider>
          </StylesThemeProvider>
        </ThemeProvider>
      </StyledEngineProvider>
      {import.meta.env.DEV && <TanStackRouterDevtools position="bottom-right" />}
    </React.Fragment>
  );
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
});
